import type React from 'react';
import {Box, Tooltip, Typography} from '@mui/material';
import type {Breakdown} from '../../types/categoryProfileTypes';

type Props = {data: Breakdown, money: (v: number) => string, top?: number, emptyText: string};

const BAR = '#0B1F3A';
const TRACK = '#EEF0F3';

/** Spend by one dimension as labelled bars on a track (Mithra 3.0), largest first; the rest is grouped as "Other". */
export const BreakdownChart: React.FC<Props> = ({data, money, top = 8, emptyText}) => {
    const rows = data.filter(r => r.spend !== 0);
    if (rows.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{py: 4, textAlign: 'center'}}>{emptyText}</Typography>;
    }
    const shown = rows.slice(0, top);
    const other = rows.slice(top).reduce((s, r) => s + r.spend, 0);
    const bars = other ? [...shown, {key: 'Other', spend: other}] : shown;
    const total = rows.reduce((s, r) => s + r.spend, 0);
    const max = Math.max(...bars.map(b => b.spend));

    return (
        <Box role="list" sx={{display: 'grid', gridTemplateColumns: 'minmax(80px, 30%) 1fr auto', columnGap: 1.5, rowGap: 1,
            alignItems: 'center'}}>
            {bars.map(b => (
                <Box key={b.key || '(not set)'} role="listitem" sx={{display: 'contents'}}>
                    <Typography variant="body2" noWrap title={b.key || '(not set)'}>{b.key || '(not set)'}</Typography>
                    <Tooltip title={`${money(b.spend)} · ${(b.spend / total * 100).toFixed(1)}% of spend`} placement="top">
                        <Box sx={{height: 10, bgcolor: TRACK, borderRadius: 0.5}}>
                            <Box sx={{height: '100%', width: `${Math.max(1, b.spend / max * 100)}%`, bgcolor: BAR, borderRadius: 0.5}}/>
                        </Box>
                    </Tooltip>
                    <Typography variant="body2" sx={{fontWeight: 600, textAlign: 'right', minWidth: 56}}>{money(b.spend)}</Typography>
                </Box>
            ))}
        </Box>
    );
};
